import type { QueryClient } from '@tanstack/react-query';

import type { IMediaItem } from '@/models/Media';
import { KlipyApi } from '@/services/media/providers/klipy/api';
import type { IKlipyGif } from '@/services/media/providers/klipy/types';

/** Max time page loading waits for GIF metadata before proceeding without it. */
const WARM_TIMEOUT_MS = 2000;

/**
 * Query options for a single GIF's metadata, shared by KlipyGifView and
 * useKlipyGifMetadataReady so both observe the same cache entry.
 * Deliberately one slug per request: messages store share-suffixed slugs
 * ("<canonical>--<token>") while /gifs/items responds with canonical slugs,
 * so a multi-slug request can't be matched back to its slugs without fragile
 * canonical-prefix parsing (burned task 300 on 2026-06-17 and task 341 on
 * 2026-07-10). Single-slug avoids matching entirely via items[0].
 */
export const getKlipyGifQueryOptions = (apiKey: string | undefined, slug: string) => ({
  queryKey: ['klipy', 'gif', slug, apiKey] as const,
  queryFn: async (): Promise<IKlipyGif | null> => {
    const items = await KlipyApi.gifs.itemsBySlugs(apiKey!, [slug]);
    return items[0] ?? null;
  },
  enabled: !!apiKey && !!slug,
  // GIF metadata is immutable, so cache entries never go stale and survive
  // unmounted conversations for the session (a few KB per GIF).
  staleTime: Infinity,
  gcTime: 24 * 60 * 60 * 1000,
});

/** Klipy GIF slugs referenced by the given media items (deduped, order preserved). */
export const getKlipyGifSlugs = (mediaItems: (IMediaItem | undefined | null)[]): string[] => {
  const slugs = mediaItems
    .filter((media): media is IMediaItem & { Id: string } =>
      Boolean(media && media.Provider === 'klipy' && media.Type === 'gif' && media.Id)
    )
    .map((media) => media.Id);
  return [...new Set(slugs)];
};

/**
 * Prefetches the given media items' uncached GIF metadata (dimensions +
 * preview URL) into the per-slug entries KlipyGifView reads. Awaited inside a
 * screen's page queryFn so a page only commits once its GIF bubbles can mount
 * at final size — the first layout is stable and history prepends can't shift
 * the scroll position. Capped at WARM_TIMEOUT_MS: stragglers render their
 * placeholder and settle individually. No-op without a usable API key or when
 * everything is already cached. Klipy is currently the only registered media
 * provider; generalize behind IMediaProvider/mediaRegistry if a second lands.
 */
export async function warmKlipyGifMetadata(
  queryClient: QueryClient,
  apiKey: string | undefined,
  mediaItems: (IMediaItem | undefined | null)[]
): Promise<void> {
  if (!apiKey) return;

  const slugs = getKlipyGifSlugs(mediaItems).filter(
    (slug) => queryClient.getQueryData(getKlipyGifQueryOptions(apiKey, slug).queryKey) === undefined
  );
  if (slugs.length === 0) return;

  await Promise.race([
    Promise.all(
      slugs.map((slug) => queryClient.prefetchQuery(getKlipyGifQueryOptions(apiKey, slug)))
    ),
    new Promise((resolve) => setTimeout(resolve, WARM_TIMEOUT_MS)),
  ]);
}
